/**
 * /select-tenant — сторінка вибору активного tenant'а після login.
 *
 * Логіка (за спецою 3.0.5 розд. 1.1):
 * - 0 tenants → redirect /no-tenants.
 * - 1 tenant  → автоматично виставляємо active_tenant_id і → / (навіть з
 *   ?reason=membership_lost: показувати warning немає сенсу, бо вибору
 *   немає; user усе одно йде у єдиний доступний).
 * - N tenants → показуємо UI вибору; якщо `?reason=membership_lost` —
 *   warning над списком + одразу зчищаємо query (щоб refresh не показав
 *   знову, інакше user «застрягає» з повідомленням про вже неактуальну
 *   подію).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getMyTenants, type MyTenantItem } from '@/api/auth';
import { useAuth } from '@/auth/store';

export default function SelectTenantPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const setActiveTenant = useAuth((s) => s.setActiveTenant);

  // Зчитуємо `?reason=membership_lost` ОДИН РАЗ при mount і зберігаємо
  // у локальний state. Так refresh сторінки (F5) НЕ показує повідомлення
  // знову — воно з'являється тільки при першому переході з 403-handler'а.
  // Сам query-параметр чистимо нижче через setParams.
  const [showMembershipLost] = useState(() => params.get('reason') === 'membership_lost');

  const tenants = useQuery<MyTenantItem[]>({
    queryKey: ['my-tenants'],
    queryFn: getMyTenants,
  });

  // Автоперенаправлення для 0/1 tenants.
  useEffect(() => {
    if (!tenants.data) return;
    if (tenants.data.length === 0) {
      navigate('/no-tenants', { replace: true });
    } else if (tenants.data.length === 1) {
      // 1 tenant — обираємо автоматично. Навіть при ?reason=membership_lost
      // показувати warning немає сенсу: вибору в user'а немає, він
      // одразу потрапляє у єдиний доступний tenant. Warning доречний
      // лише коли є кілька варіантів і user має зробити вибір.
      const onlyTenant = tenants.data[0]!;
      setActiveTenant(onlyTenant.id);
      navigate('/', { replace: true });
    }
  }, [tenants.data, navigate, setActiveTenant]);

  // Чистимо `?reason=membership_lost` із URL після першого рендеру,
  // щоб у history залишився чистий /select-tenant. showMembershipLost
  // зафіксований у useState — UI продовжує показуватись.
  useEffect(() => {
    if (params.get('reason')) {
      const next = new URLSearchParams(params);
      next.delete('reason');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pickTenant(t: MyTenantItem) {
    setActiveTenant(t.id);
    navigate('/', { replace: true });
  }

  if (tenants.isLoading) {
    return (
      <div className="login">
        <div className="card" style={{ width: 480 }}>
          Завантаження…
        </div>
      </div>
    );
  }

  if (tenants.isError) {
    return (
      <div className="login">
        <div className="card" style={{ width: 480 }}>
          <h2>Не вдалося завантажити список акаунтів</h2>
          <div className="error">{String(tenants.error)}</div>
        </div>
      </div>
    );
  }

  const list = tenants.data ?? [];

  // 0 або 1 tenant — useEffect виконає редирект, рендер пустий.
  if (list.length <= 1) {
    return null;
  }

  // N tenants — UI вибору.
  return (
    <div className="login">
      <div
        className="card"
        style={{ width: 520, padding: '2rem', background: 'white' }}
      >
        <h2 style={{ marginTop: 0 }}>Виберіть клієнта для роботи</h2>
        {showMembershipLost && (
          <div
            style={{
              background: 'var(--primary-soft)',
              color: 'var(--text-secondary)',
              padding: '0.6rem 0.8rem',
              borderRadius: 'var(--radius-sm)',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
Активуйте іншого клієнта зі списку нижче.
          </div>
        )}
        <div role="listbox" style={{ marginTop: '1rem' }}>
          {list.map((t) => (
            <button
              key={t.id}
              type="button"
              role="option"
              aria-selected="false"
              onClick={() => pickTenant(t)}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '0.85rem 1rem',
                marginBottom: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                background: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span style={{ fontWeight: 700 }}>{t.name}</span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                {t.display_role}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
